import { useFragment } from "react-relay";
import { graphql } from "relay-runtime";
import { TimelineFragment$key } from "./__generated__/TimelineFragment.graphql";
import { FormattedDate, FormattedMessage, useIntl } from "react-intl";
import * as ToolTip from "@/kit/ui/tooltip";
import { cn } from "../utils/tailwind";
import { RelativeTime } from "./RelativeTime";
import { ZonedTime } from "./ZonedTime";

const TimelineFragmentDef = graphql`
  fragment TimelineFragment on Timeline {
    startDate
    endDate
    timezone
    online
    steps {
      edges {
        node {
          label
          date
        }
      }
    }
  }
`;

interface Props {
  timeline: TimelineFragment$key;
}

export function Timeline({ timeline: timelineFragment }: Props) {
  const intl = useIntl();
  const timeline = useFragment(TimelineFragmentDef, timelineFragment);

  const startDate = new Date(timeline.startDate);
  const endDate = new Date(timeline.endDate);

  const today = new Date();
  const totalDuration = endDate.getTime() - startDate.getTime();
  const elapsed = Math.max(
    0,
    Math.min(today.getTime() - startDate.getTime(), totalDuration),
  );
  const todayPosition = totalDuration ? (elapsed / totalDuration) * 100 : 0;

  return (
    <div className="w-full mb-12">
      <div className="flex justify-between">
        <div className="flex flex-col">
          <p className="font-bold text-lg">
            <FormattedMessage defaultMessage="Start" />
          </p>
          <p className="text-sm font-thin text-grey-500">
            {timeline.online ? (
              <ZonedTime date={startDate} timezone={timeline.timezone} />
            ) : (
              <RelativeTime date={startDate} />
            )}
          </p>
        </div>
        <div className="flex flex-col items-end">
          <p className="font-bold text-lg">
            <FormattedMessage defaultMessage="Close" />
          </p>
          <p className="text-sm font-thin text-grey-500">
            {timeline.online ? (
              <ZonedTime date={endDate} timezone={timeline.timezone} />
            ) : (
              <RelativeTime date={endDate} />
            )}
          </p>
        </div>
      </div>
      <ToolTip.Provider>
        <section
          aria-label={intl.formatMessage({ defaultMessage: "Timeline" })}
          role="group"
          className="relative py-8"
        >
          <div className="relative" role="presentation">
            <Line position={todayPosition} aria-hidden="true" />
            <div
              className="absolute"
              style={{ left: "0%", transform: "translate(-10%, -40%)" }}
            >
              <DateToolTip
                date={startDate}
                timezone={timeline.online ? timeline.timezone : undefined}
                className="bg-black"
                tooltipId="tooltip-start"
              />
            </div>
            <ol className="relative" role="list">
              {timeline.steps?.edges.map(({ node }, index) => {
                const stepDate = new Date(node.date);
                const stepPosition =
                  ((stepDate.getTime() - startDate.getTime()) / totalDuration) *
                  100;
                return (
                  <li
                    key={index}
                    className="absolute list-none"
                    style={{
                      left: `${stepPosition}%`,
                      transform: "translate(-50%, -40%)",
                    }}
                    role="listitem"
                  >
                    <DateToolTip
                      date={stepDate}
                      timezone={timeline.online ? timeline.timezone : undefined}
                      className="border-2 border-black bg-white"
                      label={node.label}
                      tooltipId={`tooltip-step-${index}`}
                    />
                  </li>
                );
              })}
            </ol>
            <div
              className="absolute"
              style={{
                left: `${todayPosition}%`,
                transform: "translate(-50%, -50%)",
              }}
              role="presentation"
              title={intl.formatMessage({ defaultMessage: "Today" })}
            >
              <Point className="bg-brand-500" aria-hidden="true" />
            </div>
            <div
              className="absolute"
              style={{ left: "100%", transform: "translate(-50%, -40%)" }}
            >
              <DateToolTip
                date={endDate}
                timezone={timeline.online ? timeline.timezone : undefined}
                className="bg-black"
                tooltipId="tooltip-end"
              />
            </div>
          </div>
        </section>
      </ToolTip.Provider>
    </div>
  );
}

interface DateToolTipProps {
  date: Date;
  tooltipId: string;
  timezone?: string;
  className?: string;
  label?: string;
}

function DateToolTip({
  date,
  tooltipId,
  className,
  label,
  timezone,
}: Readonly<DateToolTipProps>) {
  return (
    <ToolTip.Root>
      <div className="z-10 relative">
        <ToolTip.Trigger asChild>
          <button
            type="button"
            className="relative w-full focus:outline focus:outline-2 focus:outline-offset-2"
            aria-describedby={tooltipId}
          >
            <Point className={className} />
            {label && (
              <span className="absolute top-6 transform -translate-x-1/2 text-xs text-grey-600">
                {label}
              </span>
            )}
          </button>
        </ToolTip.Trigger>
        <ToolTip.Content>
          <time
            id={tooltipId}
            dateTime={date.toISOString()}
            className="mt-2 text-sm text-grey-600"
          >
            <FormattedDate
              value={date}
              timeZone={timezone}
              localeMatcher={timezone ? undefined : "best fit"}
              year="numeric"
              month="long"
              day="numeric"
              timeZoneName="shortGeneric"
            />
          </time>
        </ToolTip.Content>
      </div>
    </ToolTip.Root>
  );
}

function Line({
  position,
  ...rest
}: { position: number } & React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className="absolute top-1/2 transform -translate-y-1/2 inset-x-0 flex"
      aria-hidden="true"
      {...rest}
    >
      <div
        style={{ width: `${position}%` }}
        className="border-t border-dashed border-grey-700"
      />
      <div
        style={{ width: `${100 - position}%` }}
        className="border-t border-solid border-grey-700"
      />
    </div>
  );
}

function Point({
  className,
  ...rest
}: { className?: string } & React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn("h-4 w-4 rounded-full", className)}
      aria-hidden="true"
      {...rest}
    />
  );
}
